import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AssetVisibilityManager } from '../../../../../viewer/asset-visibility-manager';
import cameraManager from '../../../../../viewer/camera-manager';
import { Dxf } from '../../../../../viewer/elements/dxf';
import { Geometry } from '../../../../../viewer/elements/geometry';
import {
  VolumeCalculatorTool,
  VolumeCalculatorToolState,
} from '../../../../../viewer/tools/volume-calculator-tool';
import { Select } from '../../../select/select';
import { OptionLabels } from '../../../skyview/right-menu/components/option-labels';
import { SkyviewContext } from '../../../skyview/skyview-state';
import { useAssetsStore } from '../../state/assets-store';
import {
  BoundaryCalculationType,
  volumeCalculationHelper,
  VolumeCalculationInfo,
  VolumeCalculationOption,
} from './volume-calculation-helper';

interface Props {
  boundaryCalculationType: BoundaryCalculationType;
  onBoundaryCalculationChanged: (option: BoundaryCalculationType) => void;
  selectedBoundaryAsset?: Dxf | Geometry;
  onBoundaryAssetChanged: (asset: Dxf | Geometry | undefined) => void;
  selectedTarget?: VolumeCalculationOption;
  selectedTargetInfo?: VolumeCalculationInfo;
}

const renderBoundaryAssets = (boundaryAssets: Dxf[] | Geometry[]) =>
  boundaryAssets.map((asset) => ({
    id: asset.assetId,
    name: asset.name,
    value: asset.assetId,
  }));

const changeBoundaryCadAsset = async (
  assetId: string,
  callback: (asset: Dxf) => void,
  boundaryAssets: Dxf[] | undefined,
) => {
  const asset = boundaryAssets?.find((asset) => asset.assetId === assetId);

  if (asset) {
    await asset.parse();
    await AssetVisibilityManager.getInstance().toggleAsset(asset, true, false);
    callback(asset);
  }
};

const changeBoundaryAreaAsset = async (
  assetId: string,
  callback: (asset: Geometry) => void,
  boundaryAssets: Geometry[] | undefined,
) => {
  const asset = boundaryAssets?.find((asset) => asset.assetId === assetId);
  if (asset) {
    await AssetVisibilityManager.getInstance().toggleAsset(asset, true, false);
    callback(asset);
  }
};

export const BoundaryBlock = (props: Props) => {
  const { t } = useTranslation();
  const [boundaryCadAssets, setBoundaryCadAssets] = React.useState<Dxf[]>();
  const [boundaryAreaAssets, setBoundaryAreaAssets] = React.useState<Geometry[]>();
  const { assets, filterAssetsByType } = useAssetsStore();

  const { activeTool } = React.useContext(SkyviewContext);
  if (!(activeTool instanceof VolumeCalculatorTool)) {
    throw new Error('Active tool is not an instance of VolumeCalculatorTool');
  }

  useEffect(() => {
    setBoundaryCadAssets(
      volumeCalculationHelper.loadValidCadAssets(
        props.selectedTarget,
        filterAssetsByType('dxf', assets),
      ) as Dxf[],
    );
    setBoundaryAreaAssets(
      volumeCalculationHelper.loadValidAreaAssets(
        props.selectedTarget,
        filterAssetsByType('area', assets),
      ) as Geometry[],
    );
  }, [assets, filterAssetsByType, props.selectedTarget]);

  const setNoBoundaryMode = () => {
    props.onBoundaryCalculationChanged(BoundaryCalculationType.NONE);
    activeTool.setVolumeCalculatorToolState(VolumeCalculatorToolState.Selecting);
  };

  const setBoundaryPolygonMode = () => {
    props.onBoundaryCalculationChanged(BoundaryCalculationType.POLYGON);
    props.onBoundaryAssetChanged(undefined);
    if (props.selectedTarget?.asset) {
      cameraManager().getActiveController()!.focusOnAsset(props.selectedTarget.asset);
      activeTool.setSelectedAsset(props.selectedTarget.asset);

      activeTool.setSelectedAsset(props.selectedTarget.asset);
      activeTool.resetTargetForVolumeBoundaryPolygon(props.selectedTarget.asset);
      activeTool.setVolumeCalculatorToolState(VolumeCalculatorToolState.DefineCadBoundingPolygon);
    }
  };

  const setBoundaryAreaMode = () => {
    props.onBoundaryCalculationChanged(BoundaryCalculationType.AREA);
    if (props.selectedTarget?.asset) {
      activeTool.resetTargetForVolumeBoundaryPolygon(props.selectedTarget.asset);
      activeTool.setVolumeCalculatorToolState(VolumeCalculatorToolState.SelectAreaBoundingAsset);
    }
  };

  const setBoundaryCadMode = () => {
    if (props.selectedTarget?.asset) {
      cameraManager().getActiveController()!.focusOnAsset(props.selectedTarget.asset);
      activeTool.setSelectedAsset(props.selectedTarget.asset);
    }
    props.onBoundaryCalculationChanged(BoundaryCalculationType.CAD);
    activeTool.setVolumeCalculatorToolState(VolumeCalculatorToolState.SelectCadBoundingAsset);
  };

  const changeBoundaryCalculationMode = (mode: BoundaryCalculationType) => {
    switch (mode) {
      case BoundaryCalculationType.NONE:
        setNoBoundaryMode();
        break;
      case BoundaryCalculationType.POLYGON:
        setBoundaryPolygonMode();
        break;
      case BoundaryCalculationType.AREA:
        setBoundaryAreaMode();
        break;
      case BoundaryCalculationType.CAD:
        setBoundaryCadMode();
        break;
    }
  };

  return (
    <>
      <OptionLabels
        active={props.boundaryCalculationType}
        itemRows={[
          [
            {
              title: t('boundaryType.none', { ns: 'skyviewVolumeCalculation' }),
              value: BoundaryCalculationType.NONE,
              onSelect: () => {
                changeBoundaryCalculationMode(BoundaryCalculationType.NONE);
              },
            },
          ],
          [
            {
              title: t('boundaryType.polygon', { ns: 'skyviewVolumeCalculation' }),
              value: BoundaryCalculationType.POLYGON,
              disabled: !props.selectedTarget || !props.selectedTargetInfo,
              onSelect: () => {
                changeBoundaryCalculationMode(BoundaryCalculationType.POLYGON);
              },
            },
            {
              title: t('boundaryType.area', { ns: 'skyviewVolumeCalculation' }),
              value: BoundaryCalculationType.AREA,
              onSelect: () => {
                changeBoundaryCalculationMode(BoundaryCalculationType.AREA);
              },
            },
            {
              title: t('boundaryType.cad', { ns: 'skyviewVolumeCalculation' }),
              value: BoundaryCalculationType.CAD,
              disabled: !props.selectedTarget || !props.selectedTargetInfo,
              onSelect: () => {
                changeBoundaryCalculationMode(BoundaryCalculationType.CAD);
              },
            },
          ],
        ]}
      />
      {[BoundaryCalculationType.CAD, BoundaryCalculationType.AREA].includes(
        props.boundaryCalculationType,
      ) && (
        <Select
          options={
            props.boundaryCalculationType === BoundaryCalculationType.CAD && boundaryCadAssets
              ? renderBoundaryAssets(boundaryCadAssets)
              : props.boundaryCalculationType === BoundaryCalculationType.AREA && boundaryAreaAssets
                ? renderBoundaryAssets(boundaryAreaAssets)
                : []
          }
          placeholderText={' '}
          value={props.selectedBoundaryAsset?.assetId}
          onChange={async (e) => {
            const value = e.target.value;
            if (props.boundaryCalculationType === BoundaryCalculationType.CAD) {
              await changeBoundaryCadAsset(value, props.onBoundaryAssetChanged, boundaryCadAssets);
            } else if (props.boundaryCalculationType === BoundaryCalculationType.AREA) {
              await changeBoundaryAreaAsset(
                value,
                props.onBoundaryAssetChanged,
                boundaryAreaAssets,
              );
            }
          }}
        />
      )}
    </>
  );
};
